import { Builder } from '@builder.io/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'
import { HeadingClass, HeadingType } from 'enums'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import React from 'react'
import Slider from 'react-slick'
import Bullet from '../../../images/BulletPoint.svg'
import LinkRoute from '../../ui/link/Link'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

import './index.css'
import { triggerTagging } from 'components/util/common-utils'
import { cn } from 'utils/classnames'
import CdnImage from '../../util/CdnImage'

export interface IProps {
  headerImage: any
  headerImageMobile: any
  headingText: string
  headingTextTwo: string
  headingTextDesktop: string
  headingTextColor: string
  headingType: HeadingType
  headingClass: HeadingClass
  subHeading: string
  recipeDescription: string
  backgroundColor: string
  articleTitleTextColor: string
  articleDescriptionTextColor: string
  articleItemBackgroundColor: string
  maxNumberToList: number
  recipesAvailable: any
  autoplayTime: number
  recipeBulletPoint: any
  bulletAltText: string
  maxNumberOfFeature: number
  isCarousel: boolean
  showCarouselArrows: boolean
  enableButton: boolean
  buttonWidth: string
  coupon: string
  roundedImage: boolean
  recipeListerNewStyle: boolean
}

const RecipeListerAlternate = ({
  headerImage,
  headerImageMobile,
  headingText,
  headingTextTwo,
  headingTextDesktop,
  headingTextColor,
  headingType,
  headingClass,
  subHeading,
  backgroundColor,
  articleTitleTextColor,
  articleDescriptionTextColor,
  articleItemBackgroundColor,
  maxNumberToList,
  recipesAvailable,
  autoplayTime,
  recipeBulletPoint,
  bulletAltText = '',
  maxNumberOfFeature = 3,
  isCarousel,
  showCarouselArrows = false,
  enableButton,
  buttonWidth,
  coupon = '',
  roundedImage,
  recipeListerNewStyle = false
}: IProps) => {
  const dogDetailsPage = 'dog-details'

  function SamplePrevArrowDesktop(props: any) {
    const { className, style, onClick } = props

    const arrowStyle = {
      ...style,
      display: 'flex',
      justifyContent: 'center',
      zIndex: 2,
      left: '-14px',
      top: '50%',
      transform: 'translateY(-50%)'
    }

    return (
      <>
        {!recipeListerNewStyle && (
          <div
            className={`${className} ${recipeListerNewStyle ? 'hidden' : ''}`}
            style={arrowStyle}
            onClick={onClick}
          />
        )}

        {recipeListerNewStyle && (
          <div
            className="flex justify-center"
            style={{
              ...arrowStyle,
              position: 'absolute'
            }}
            onClick={onClick}
          >
            <StaticImage className="w-6" src="../../../../images/dog-details/dog-details-back.svg" alt="Chevron Left" />
          </div>
        )}
      </>
    )
  }

  function SampleNextArrowDesktop(props: any) {
    const { className, style, onClick } = props

    const arrowStyle = {
      ...style,
      display: 'flex',
      justifyContent: 'center',
      zIndex: 2,
      right: '-14px',
      top: '50%',
      transform: 'translateY(-50%)'
    }

    return (
      <>
        {!recipeListerNewStyle && (
          <div
            className={`${className} ${recipeListerNewStyle ? 'hidden' : ''}`}
            style={arrowStyle}
            onClick={onClick}
          />
        )}

        {recipeListerNewStyle && (
          <div
            className="flex justify-center"
            style={{
              ...arrowStyle,
              position: 'absolute'
            }}
            onClick={onClick}
          >
            <StaticImage
              className="w-6 rotate-180"
              src="../../../../images/dog-details/dog-details-back.svg"
              alt="Chevron Right"
            />
          </div>
        )}
      </>
    )
  }

  const SamplePrevArrow = (props: any) => {
    const { style, onClick, disabled } = props
    return (
      <div
        id={recipeListerNewStyle ? 'recipeArrowRightAlternate' : 'reviewArrowRight'}
        style={{
          ...style,
          display: 'inline-flex',
          justifyContent: 'center',
          zIndex: 2,
          transform: 'translateY(-50%)',
          width: '1.6rem',
          height: '1.6rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        onClick={disabled ? undefined : onClick}
      >
        <StaticImage className="w-8" src={'../../../../images/dog-details/dog-details-back.svg'} alt="Chevron left" />
      </div>
    )
  }

  const SampleNextArrow = (props: any) => {
    const { style, onClick, disabled } = props
    return (
      <div
        id={recipeListerNewStyle ? 'recipeArrowLeftAlternate' : 'reviewArrowLeft'}
        style={{
          ...style,
          display: 'inline-flex',
          justifyContent: 'center',
          zIndex: 2,
          transform: 'translateY(-50%)',
          width: '1.6rem',
          height: '1.6rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        onClick={disabled ? undefined : onClick}
      >
        <StaticImage
          className="w-8 rotate-180"
          src={'../../../../images/dog-details/dog-details-back.svg'}
          alt="Chevron right"
        />
      </div>
    )
  }

  const carouselSettings = {
    dots: false,
    autoplay: true,
    infinite: true,
    speed: 500,
    slidesToShow: recipeListerNewStyle ? 3 : 4,
    slidesToScroll: 1,
    arrows: showCarouselArrows,
    ...(showCarouselArrows && {
      nextArrow: <SampleNextArrowDesktop />,
      prevArrow: <SamplePrevArrowDesktop />
    }),
    pauseOnHover: true
  }

  const carouselSettingsMobile = {
    dots: false,
    autoplay: true,
    infinite: recipeListerNewStyle ? false : true,
    speed: 500,
    autoplaySpeed: 3000,
    slidesToShow: recipeListerNewStyle ? 1.5 : 2,
    slidesToScroll: 1,
    arrows: showCarouselArrows,
    pauseOnHover: true,
    centerMode: false,
    variableWidth: false,
    ...(showCarouselArrows && {
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />
    })
  }

  const { allBuilderModels }: any = useStaticQuery(graphql`
    query {
      allBuilderModels {
        recipe {
          data {
            url
            title
            cloudinaryCookedImage
            cloudinaryPreCookImage
            cdnCookedImage
            cdnPreCookedImage
            features
            isHealthPlus
            description
            highlights
          }
        }
      }
    }
  `)

  const getRecipesToDisplay = (recipeArticles: any[], recipesAvailable: any[]) => {
    return recipeArticles.filter((rec: any) => {
      const articleTitle = rec.data?.title?.trim?.()
      return recipesAvailable.some((r: any) => r.recipeTitle?.trim() === articleTitle)
    })
  }

  const getRecipes = () => {
    const recipeArticles =
      maxNumberToList === 0 || !maxNumberToList
        ? allBuilderModels.recipe
        : allBuilderModels.recipe?.slice(0, maxNumberToList)

    let recipesToDisplay = recipeArticles

    if (recipesAvailable?.length > 0) {
      recipesToDisplay = getRecipesToDisplay(recipeArticles, recipesAvailable)
    }

    return recipesToDisplay?.map((item: any, i: number) => {
      const recipe = item.data

      //  Need to pass new key of "features" for recipe in builder or grapgQL builder query
      const recipeFeature =
        maxNumberOfFeature === 0 || !maxNumberOfFeature
          ? item?.data?.features
          : item?.data?.features?.slice(0, maxNumberOfFeature)
      const _recipeHighlights =
        maxNumberOfFeature === 0 || !maxNumberOfFeature
          ? item?.data?.highlights
          : item?.data?.highlights?.slice(0, maxNumberOfFeature)
      return (
        <div className="mx-auto !w-11/12 max-w-2xl py-4 px-0 sm:py-4 lg:max-w-7xl lg:px-2" key={i}>
          <div
            className={`recipe-lister-item group relative flex flex-col rounded-2xl ${recipeListerNewStyle ? 'min-h-[24rem]' : 'min-h-[31rem] 2xl:min-h-[29rem] pb-4'}`}
            key={i}
            style={{ backgroundColor: articleItemBackgroundColor }}
          >
            {recipe.isHealthPlus && (
              <span
                style={{
                  fontSize: '0.65rem',
                  lineHeight: '1rem',
                  top: recipeListerNewStyle ? '1rem' : '0.5rem',
                  right: recipeListerNewStyle ? '1rem' : '0.5rem'
                }}
                className="absolute z-30 inline-flex h-11 w-11 -rotate-12 items-center justify-center rounded-full bg-ddGreen text-center drop-shadow md:h-14 md:w-14 xl:inline-flex"
              >
                Health<span className="font-extrabold">+</span>
              </span>
            )}
            <LinkRoute href={recipe.url}>
              <div
                key={`div-${recipe?.title}`}
                className="my-o relative mx-auto h-48 w-full overflow-hidden group-hover:opacity-75 sm:h-60 lg:aspect-none"
              >
                <CdnImage
                  className={`${roundedImage ? 'rounded-2xl' : 'rounded-t-2xl'} object-cover object-center block`}
                  url={recipe?.cdnPreCookedImage}
                  alt={recipe?.title}
                  steps={[330]}
                  style={{
                    width: '100%',
                    height: '330px',
                    maxHeight: '100%'
                  }}
                />
              </div>
            </LinkRoute>
            <div className="mt-1 h-full px-4 sm:px-5">
              <div
                className={`flex h-full flex-col ${recipeListerNewStyle ? 'text-left' : 'justify-between text-center'} lg:text-left`}
              >
                <h4
                  className={`text-base text-gray-700 sm:text-lg ${recipeListerNewStyle ? '!font-sans pt-4 h-12 text-left font-semibold' : 'h-20'}`}
                  style={{ color: articleTitleTextColor }}
                >
                  <LinkRoute href={recipe.url}>{recipe.title}</LinkRoute>
                </h4>
                {/* <ul className="mt-2">
                  {recipeHighlights?.length > 0 &&
                    recipeHighlights?.map((item: any, i: number) => (
                      <li className="my-2 flex items-end" key={i}>
                        <p className="mx-auto mt-1 inline-flex justify-center rounded-full bg-[#FCD1CD] py-2 px-2 text-center text-xs xs:w-full md:text-sm">
                          {item.highlight}
                        </p>
                      </li>
                    ))}
                </ul> */}
                <p
                  className={`mt-1 text-sm text-gray-500 ${recipeListerNewStyle ? 'border-b border-gray-300 h-20 text-left' : 'flex-1'}`}
                  style={{ color: articleDescriptionTextColor }}
                >
                  {recipe.description}
                </p>
                <ul
                  className={`relative bottom-4 mx-auto ${recipeListerNewStyle ? 'w-full mt-6' : 'w-11/12  mt-8'} -ml-2 flex flex-col items-left justify-center`}
                >
                  {recipeFeature?.length > 0 &&
                    recipeFeature?.map((item: any, i: number) => (
                      <li className={`flex items-center ${recipeListerNewStyle ? 'my-1' : ''}`} key={i}>
                        <img
                          src={recipeBulletPoint || Bullet}
                          className={`mr-2 h-4 w-4 items-end ${recipeListerNewStyle ? 'lg:h-4 lg:w-4' : 'lg:h-8 lg:w-8'}`}
                          alt={bulletAltText}
                        />
                        <p
                          className={`mx-auto text-center text-xs lg:ml-1 lg:text-left lg:text-sm ${recipeListerNewStyle ? 'text-black font-bold' : ''}`}
                        >
                          {item.feature}
                        </p>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    })
  }
  const getRecipesMobile = () => {
    const recipeArticles =
      maxNumberToList === 0 || !maxNumberToList
        ? allBuilderModels.recipe
        : allBuilderModels.recipe?.slice(0, maxNumberToList)

    let recipesToDisplay = recipeArticles

    if (recipesAvailable?.length > 0) {
      recipesToDisplay = getRecipesToDisplay(recipeArticles, recipesAvailable)
    }

    return recipesToDisplay?.map((item: any, i: number) => {
      const recipe = item.data

      //  Need to pass new key of "features" for recipe in builder or grapgQL builder query
      const recipeFeature =
        maxNumberOfFeature === 0 || !maxNumberOfFeature
          ? item?.data?.features
          : item?.data?.features?.slice(0, maxNumberOfFeature)

      const _recipeHighlights =
        maxNumberOfFeature === 0 || !maxNumberOfFeature
          ? item?.data?.highlights
          : item?.data?.highlights?.slice(0, maxNumberOfFeature)

      return (
        <div className="mx-auto max-w-2xl py-4 px-0 sm:py-4 lg:max-w-7xl lg:px-4" key={i}>
          <div
            className={`recipe-lister-item group relative flex flex-col rounded-2xl ${recipeListerNewStyle ? 'min-h-[25rem]' : 'min-h-[29rem]'}`}
            key={i}
            style={{ backgroundColor: articleItemBackgroundColor }}
          >
            {recipe.isHealthPlus && (
              <span
                style={{ fontSize: '0.65rem', lineHeight: '1rem', top: '0.5rem', right: '0.5rem' }}
                className="absolute z-30 inline-flex h-11 w-11 -rotate-12 items-center justify-center rounded-full bg-ddGreen text-center drop-shadow md:h-14 md:w-14 xl:inline-flex"
              >
                Health<span className="font-extrabold">+</span>
              </span>
            )}
            <LinkRoute href={recipe.url}>
              <div
                key={`div-${recipe?.title}`}
                className={`my-o relative mx-auto ${roundedImage ? 'flex justify-center items-center rounded-2xl' : 'rounded-none'} h-48 w-full overflow-hidden group-hover:opacity-75 sm:h-60 lg:aspect-none`}
              >
                <CdnImage
                  className={`${roundedImage ? 'rounded-2xl' : 'rounded-t-2xl'} object-cover object-center block ${recipeListerNewStyle ? 'w-full h-auto max-h-full' : 'w-[330px] h-[220px] max-h-full'}`}
                  url={recipe?.cdnPreCookedImage}
                  alt={recipe?.title}
                  steps={[330]}
                />
              </div>
            </LinkRoute>
            <div className={`${recipeListerNewStyle ? 'mt-2 pl-1 lg:px-5' : 'mt-5 px-4 sm:px-5'} lg:mt-5 h-full`}>
              <h4
                className={`text-base text-gray-700 sm:text-lg ${recipeListerNewStyle ? '!font-sans font-semibold h-8 lg:h-12 text-left' : 'pt-4 '} lg:pt-4`}
                style={{ color: articleTitleTextColor }}
              >
                <LinkRoute href={recipe.url}>{recipe.title}</LinkRoute>
              </h4>
              {/* <ul className="mt-2">
                {recipeHighlights?.length > 0 &&
                  recipeHighlights?.map((item: any, i: number) => (
                    <li className="my-1 flex items-end" key={i}>
                      <p className="mx-auto mt-1 inline-flex justify-center rounded-full bg-[#FCD1CD] py-2 px-2 text-center text-xs xs:w-full md:text-sm">
                        {item.highlight}
                      </p>
                    </li>
                  ))}
              </ul> */}
              <p
                className={`mb-2 text-sm text-gray-500 ${recipeListerNewStyle ? 'border-b border-gray-300 h-24 text-left' : 'flex-1 mt-4'} lg:mt-4`}
                style={{ color: articleDescriptionTextColor }}
              >
                {recipe.description}
              </p>
              <ul
                className={`relative bottom-4 mx-auto mt-8 ${recipeListerNewStyle ? 'w-full' : 'w-11/12 -ml-2'} flex flex-col items-left justify-center`}
              >
                {recipeFeature?.length > 0 &&
                  recipeFeature?.map((item: any, i: number) => (
                    <li className={`flex items-center ${recipeListerNewStyle ? 'my-1' : ''}`} key={i}>
                      <img
                        src={recipeBulletPoint || Bullet}
                        className={`mr-2 h-4 w-4 items-end ${recipeListerNewStyle ? 'lg:h-4 lg:w-4' : 'lg:h-8 lg:w-8'}`}
                        alt={bulletAltText}
                      />
                      <p
                        className={`text-xs lg:ml-1 lg:text-left lg:text-sm ${recipeListerNewStyle ? 'text-left text-black font-bold' : 'mx-auto text-center'}`}
                      >
                        {item.feature}
                      </p>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )
    })
  }

  return (
    <>
      <div
        className={`relative mx-auto hidden w-full bg-contain bg-bottom bg-no-repeat lg:w-11/12 lg:block h-28 2xl:h-48 lg:container`}
        style={{
          backgroundImage: headerImage ? `url(${headerImage})` : 'none'
        }}
      />
      <div
        className={`relative mx-auto block h-[3.35rem] xs:h-[3.5rem] sm:h-20 md:h-24 ${recipeListerNewStyle ? 'w-full' : 'w-[90%]'} -mb-8 xs:-mb-6 sm:-mb-[1.75rem] md:-mb-10 bg-contain bg-no-repeat lg:hidden`}
        style={{
          backgroundImage: headerImageMobile ? `url(${headerImageMobile})` : 'none'
        }}
      />
      {isCarousel && (
        <>
          <div
            className={`relative mx-auto ${recipeListerNewStyle ? 'w-full' : 'w-[90%]  pb-4 '} rounded-b-xl text-center md:block lg:w-11/12 lg:px-8 lg:container`}
            style={{ backgroundColor }}
          >
            <div className="mb-4 -mt-4 w-11/12 mx-auto">
              <div className="block lg:hidden">
                <CustomHeading headingClass={headingClass} headingType={headingType} color={headingTextColor}>
                  {headingText}
                </CustomHeading>
                {headingTextTwo && (
                  <CustomHeading headingClass={headingClass} headingType={headingType} color={headingTextColor}>
                    {headingTextTwo}
                  </CustomHeading>
                )}
              </div>
              <div className="hidden lg:block">
                <CustomHeading headingClass={headingClass} headingType={headingType} color={headingTextColor}>
                  {headingTextDesktop}
                </CustomHeading>
              </div>
              <div
                className={`enable-list-styles mt-4 h-full text-center text-base font-medium font-sans w-full justify-center`}
                dangerouslySetInnerHTML={{ __html: subHeading }}
              />
            </div>
            <div className="hidden lg:block">
              <Slider {...carouselSettings}>{getRecipes()}</Slider>
            </div>
            <div className={cn('block lg:hidden', { 'pb-12': showCarouselArrows })}>
              <Slider {...carouselSettingsMobile}>{getRecipesMobile()}</Slider>
            </div>
            {enableButton && (
              <div className="flex flex-col md:flex-row my-0 md:my-3 pt-10 lg:pt-0 pb-6">
                <div className="w-full md:w-1/2 flex items-center justify-center md:items-end md:justify-end">
                  <button
                    type="button"
                    className={`my-2 md:my-0 mr-0 md:mr-2 rounded-3xl border-2 border-primary bg-white px-4 py-2 hover:underline font-medium`}
                    onClick={() => {
                      window.location.href = `/recipes/${coupon ? `?coupon=${coupon}` : ''}`
                    }}
                  >
                    View more recipes
                  </button>
                </div>
                <div className="w-full md:w-1/2 flex items-center justify-center md:items-start md:justify-start">
                  <button
                    type="button"
                    className={`btn-cta my-2 md:my-0 ml-0 md:ml-2`}
                    onClick={() => {
                      // Check if there is a route section on the page
                      const matchingRouteSection = Array.from(document.querySelectorAll<HTMLElement>('[id]')).find(el =>
                        el.id.includes('Route')
                      )
                      if (matchingRouteSection) {
                        triggerTagging(`hero_dog_cta_${matchingRouteSection.id}`, `${matchingRouteSection.id}`)
                        matchingRouteSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      } else {
                        window.location.href = `/signup/${dogDetailsPage}/${coupon ? `?coupon=${coupon}` : ''}`
                      }
                    }}
                  >
                    Create Your Plan
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}

export default RecipeListerAlternate

export const RecipeListerProps = {
  name: 'RecipeListerAlternate',
  inputs: [
    {
      name: 'maxNumberToList',
      type: 'number',
      defaultValue: 0,
      description: 'Default is set to 0. When its 0 the component will display all blogs that are available.'
    },
    {
      name: 'recipesAvailable',
      friendlyName: 'Recipes available (leave blank to show all)',
      type: 'list',
      defaultValue: [
        {
          recipeTitle: null
        }
      ],
      subFields: [
        { name: 'recipeTitle', type: 'text', defaultValue: '', friendlyName: 'Recipe title (use exact recipe title)' }
      ]
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: '#F5F1ED'
    },
    {
      name: 'headerImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'headerImageMobile',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextTwo',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'headingTextDesktop',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'subHeading',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'articleTitleTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'articleDescriptionTextColor',
      type: 'color',
      defaultValue: 'gray'
    },
    {
      name: 'articleItemBackgroundColor',
      type: 'color',
      defaultValue: '#dce7f7'
    },
    {
      name: 'autoplayTime',
      type: 'number',
      defaultValue: 3000
    },
    {
      name: 'recipeBulletPoint',
      type: 'file',
      defaultValue: Bullet
    },
    {
      name: 'bulletAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'maxNumberOfFeature',
      type: 'number',
      defaultValue: 3,
      description: 'Default is set to 3. When its 0 the component will display all blogs that are available.'
    },
    {
      name: 'isCarousel',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'showCarouselArrows',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'buttonWidth',
      type: 'string',
      defaultValue: 'full'
    },
    {
      name: 'enableButton',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'roundedImage',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'recipeListerNewStyle',
      type: 'boolean',
      defaultValue: false
    }
  ]
}

Builder.registerComponent(RecipeListerAlternate, {
  ...RecipeListerProps
})
